<template>
  <div :class="[
        `mt-${props.marginTop}`,
        `mb-${props.marginBottom}`
       ]"
       ref="component"
       class="qrcode d-flex-column d-flex justify-content-center align-items-center">
    <div v-if="!active.passwd"
         class="s5">
      <img class="p"
           :class="[
               isBlobLoaded ? `active` : null,
               props.isSwal ? `swal-embedded` : null
           ]"
           :src="getImage" alt="">
      <div v-if="active.status === EnumQrcodeStatus.EXPIRE"
           class="s7 d-flex d-flex-column justify-content-center align-items-center">
        <a class="avatar delegate bg-light-danger float-start waves-effect waves-float waves-light">
          <div class="avatar-content">
            <icon-sax type="security-safe" />
          </div>
        </a>
        <p class="ft-13 m-b-3 text-danger"
           v-html="$t(`component.qrcode.status.expire.subject`)" />
        <small @click="fetch"
               class="cursor hand text-hyperlink"
               v-html="$t(`component.qrcode.status.expire.text`)" />
      </div>
      <div v-else-if="active.status === EnumQrcodeStatus.INSPECTION"
           class="s7 d-flex d-flex-column justify-content-center align-items-center">
        <a class="avatar delegate bg-light-success float-start waves-effect waves-float waves-light">
          <div class="avatar-content">
            <icon-sax type="shield-tick" />
          </div>
        </a>
        <p class="ft-13 m-b-3 text-success"
           v-html="$t(`component.qrcode.status.inspection.subject`)" />
        <small class="text-secondary"
               v-html="$t(`component.qrcode.status.inspection.text`)" />
      </div>
      <div v-else-if="active.status === EnumQrcodeStatus.AUTHORIZED"
           class="s7 d-flex d-flex-column justify-content-center align-items-center">
        <a class="avatar delegate bg-light-success float-start waves-effect waves-float waves-light">
          <div class="avatar-content">
            <icon-sax type="check" />
          </div>
        </a>
        <p class="ft-13 m-b-3 text-success"
           v-html="$t(`component.qrcode.status.authorize.subject`)" />
        <small class="text-secondary"
               v-html="$t(`component.qrcode.status.authorize.text`)" />
      </div>
    </div>
    <div v-else
         class="s5 w-100">
      <form-password
          name="passwd"
          @keyup="onKeyUp"
          @keydown.stop.prevent.enter="emit(`submit`, field.passwd)"
          :bottom="0"
          :source="field"
          :holder="props.placeholder" />
    </div>
    <div v-if="!props.isSwal"
         class="mt-2 d-flex d-flex-column justify-content-center">
      <p class="m-0"
         v-safe-html="sprintf(
             $t(getSubject),
             props.data.organization.name)" />
      <div class="text-center m-t-10">
        <router-link
            target="_blank"
            :to="{name: `App`}"
            class="logo badge badge-light-muted cursor-pointer">
          <img v-if="props.data.organization.uuid"
               :src="`/img/organization/${props.data.organization.seq}/logo.png`"
               alt="">
          <span class="ft-12 text-secondary m-l-5"
                v-safe-html="sprintf(
                    $t(getText),
                    props.data.organization.name)" />
        </router-link>
      </div>
    </div>
    <div class="mt-2" v-else>
      <small v-if="!active.passwd"
             class="text-muted text-center">
        {{$t(`component.swal.to.qrcode.subject`)}}
        <span @click="onChangeMode"
              class="text-hyperlink cursor hand"
              v-html="$t(`component.swal.to.qrcode.text`)" />
      </small>
      <small v-else
             class="text-muted text-center">
        {{$t(`component.swal.to.passwd.subject`)}}
        <span @click="onChangeMode"
              class="text-hyperlink cursor hand"
              v-html="$t(`component.swal.to.passwd.text`)" />
      </small>
    </div>
  </div>
</template>

<script setup>

import $ from "jquery"

import IconSax from "@/component/IconSax.vue"
import FormPassword from "@/component/form/FormPassword.vue"
import factoryLoading from "@/asset/loading/Spinner-1s-200px.png"
import {sprintf} from "sprintf-js"
import {computed, nextTick, onMounted, onUnmounted, reactive, ref, watch} from "vue"
import {useUtils} from "@/hook/useUtils"
import {usePermission} from "@/hook/usePermission"
import {useRouter} from "vue-router"
import {useUser} from "@/hook/useUser"
import {useSetting} from "@/hook/useSetting"
import {useSse} from "@/hook/useSse"
import {EnumUserSettingAuthorized} from "@/enum/user";
import http from "@/api";

const {
  getUser,
  setUserMapping
} = useUser()

const {
  setLogin
} = usePermission()

const {
  setSettingAuthorized
} = useSetting()

const {
  getUuid
} = useUtils()

/**
 * 枚舉（僅本元件使用 → 內聯定義，不另立 enum 模組）
 */

/**
 * 二維碼 - 產生
 */
const EnumQrcodeMethod = Object.freeze({
  //二級驗證
  FACTORY: "factory",
  //用戶綁定管理員
  MAPPING: "mapping"
})

/**
 * 二維碼 - 顯示端輪詢 - 回應狀態
 *
 * 對應後端 FactoryController 的 FACTORY_STATUS_STRING_* 常數
 */
const EnumQrcodeRespondStatus = Object.freeze({
  //尚未掃描 / 已過期
  PENDING: "pending",
  //已掃描待授權
  INSPECTED: "inspected",
  //已授權 (附帶登入權杖)
  AUTHORIZED: "authorized",
  //已取消
  CANCELLED: "cancelled"
})

/**
 * 掃碼登入 - SSE 事件名
 *
 * 對應後端 sse/SseEvent.java 的 FACTORY_* 常數（逐字相同）。
 * ⚠️ 事件本身不帶登入權杖，只是「狀態變了」的訊號；權杖仍由 /api/factory/qrcode/status 領取。
 */
const EnumQrcodeStreamEvent = Object.freeze({
  INSPECTION: `factoryInspection`,
  AUTHORIZE: `factoryAuthorize`,
  CANCEL: `factoryCancel`,
})

/**
 * 二維碼 - 狀態
 */
const EnumQrcodeStatus = Object.freeze({
  //已驗證
  INSPECTION: 1,
  //已授權
  AUTHORIZED: 2,
  //超時
  EXPIRE: 3
})

/**
 * 顯示端輪詢掃碼狀態的間隔 (毫秒)
 */
const POLLING_INTERVAL = 1500

/**
 * API（僅本元件使用 → 內聯實作，不另立 api/module）：掃碼狀態 GET /api/factory/qrcode/status
 * 每 POLLING_INTERVAL 輪詢一次，故關閉讀取遮罩（沿用原模組設定）
 * ※ 同 module 的 getFactory 由 useSwalAlert 與 v2/hooks/sweetalert 共用，仍保留於 api/module
 */
const apiQrcodeStatus = (token) =>
    http.get(`/api/factory/qrcode/status`, {token}, {opts: {builtIn: {loadingMask: false}}})

const router = useRouter()

const {startSse, stopSse} = useSse()

/**
 * 參數 - 外部 - 提交
 */
const emit = defineEmits(["submit", "cancel", "authorize", "keyup", "changeMode"])

const component = ref()

const props = defineProps({
  //傳入前置參數
  data: {
    type: Object,
    default: () => ({
      organization: {
        uuid: null,
        name: null
      },
      available: true,
      uuid: null,
      name: null,
      avatarUrl: null
    })
  },
  //授權完成延遲關閉 (為了看特效, 不需要可以設定0)
  delay: {type: Number, default: 550},
  //是否為管理員綁定
  isMapping: {type: Boolean, default: false},
  //是否為嵌入sweet alert
  isSwal: {type: Boolean, default: false},
  //取消時順便關閉swal
  isCancelClose: {type: Boolean, default: false},
  //頂部加塞
  marginTop: {type: Number, default: 0},
  //底部加塞
  marginBottom: {type: Number, default: 2},
  //改為密碼輸入時的占位符
  placeholder: {type: String, default: ""}
})

const field = reactive({
  passwd: ""
})

const active = reactive({
  //狀態
  status: null,
  //是否為密碼輸入模式, 需要 props.isSwal === true 才有效
  passwd: false,
  //超時計數器
  timer: null,
  //掃碼狀態輪詢計數器
  polling: null,
  //連線權證
  token: null,
  //二維碼超時時間
  expire: parseInt(import.meta.env.VITE_FACTORY_QRCODE_EXPIRE),
  //二維碼base64內容
  blob: null
})

//密碼監聽
const onKeyUp = () => emit(`keyup`, field.passwd)

//切換模式監聽
const onChangeMode = () => {
  active.passwd = !active.passwd
  emit("changeMode", !active.passwd)
}

//掃碼檢驗成功, UI顯示掃碼成功
const inspection = () => active.status = EnumQrcodeStatus.INSPECTION

//用戶端在App上按確認授權 (完成準備跳轉)
const authorize = (token) => {

  //顯示登入成功
  active.status = EnumQrcodeStatus.AUTHORIZED

  setTimeout(()=>{

    //如果是綁定
    if (props.isMapping) {

      //強迫登入改為Qrcode預設
      setSettingAuthorized(EnumUserSettingAuthorized.QRCODE)

      //修改為用戶與管理員對應成功
      setUserMapping(true)

    }
    //如果是二級驗證（權杖已由 /api/factory/qrcode/status 寫進 cookie）
    else if (token) setLogin(true)

    //非 sweet alert 崁入, 跳轉
    if (!props.isSwal) router.push({
      name: `AdminDashboard`,
    })
    //sweet alert 崁入, 呼叫 emit
    else emit(`authorize`)

  }, props.delay)

}

//取消
const cancel = () => {

  //如果是 sweet alert 崁入 + 指定手機取消關閉, 傳送關閉訊號
  if (props.isSwal && props.isCancelClose) emit(`cancel`)
  //其他狀況, 呼叫超時即可
  else expire()

}

//設定超時
const expire = () => {

  //清除計時器
  clearInterval(active.timer)

  //停止輪詢 (二維碼已失效, 再輪也只會拿到 pending)
  stopPolling()

  //狀態 - 已經超時
  active.status = EnumQrcodeStatus.EXPIRE

}

//停止輪詢掃碼狀態（含即時推播連線）
//
//⚠️ 這裡刻意把 stopSse() 一起做掉：呼叫端有五處（超時 / 重取二維碼 / 已授權 / 已取消 / 元件卸載），
//   其中「已授權」那處的註解寫著「權杖為一次性領取, 必須先停止輪詢」——
//   接上 SSE 之後，那條不變式必須連推播一起停，否則下一則事件會再去領一次已經 evict 的權杖。
//   把兩者綁在同一個函式裡，五個呼叫點就都不用改，也不會有人漏掉。
const stopPolling = () => {

  stopSse()

  if (active.polling) clearInterval(active.polling)

  active.polling = null

}

//開始監看掃碼狀態
//
//優先走 SSE 即時推播；連不上（舊瀏覽器 / 代理把長連線切掉）才退回輪詢。
//⚠️ 推播事件刻意不帶登入權杖，只當「狀態變了」的訊號 —— 收到後仍呼叫 onPolling()
//   去 /api/factory/qrcode/status 領取，那支端點「終態領取後即 evict」的一次性語意因此完全不變。
//   兩條路走同一段判斷邏輯，也就不可能出現「推播版與輪詢版行為不一致」。
const startPolling = () => {

  //避免重複啟動
  stopPolling()

  //沒有 token 就沒有東西可監看（正常流程不會發生, 但別讓它連出一條 token=undefined 的無效連線）
  if (!active.token) return

  const url = `/api/factory/qrcode/stream?token=${active.token}`

  const connected = startSse(
      url,
      [EnumQrcodeStreamEvent.INSPECTION, EnumQrcodeStreamEvent.AUTHORIZE, EnumQrcodeStreamEvent.CANCEL],
      () => onPolling(),
      () => startPollingByInterval()
  )

  if (!connected) startPollingByInterval()

}

//降級：定時輪詢（SSE 不可用時的後路，程式碼刻意保留）
const startPollingByInterval = () => {

  if (active.polling) clearInterval(active.polling)

  active.polling = setInterval(() => onPolling(), POLLING_INTERVAL)

}

//輪詢掃碼狀態
const onPolling = async () => {

  if (!active.token) return

  const {
    data
  } = await apiQrcodeStatus(active.token)

  //取不到內容 (連線異常), 下一輪再試
  if (!data?.status) return

  //已掃描待授權, UI顯示掃碼成功
  if (data.status === EnumQrcodeRespondStatus.INSPECTED) inspection()
  //用戶端在App上按確認授權 (權杖為一次性領取, 必須先停止輪詢)
  else if (data.status === EnumQrcodeRespondStatus.AUTHORIZED) {
    stopPolling()
    clearInterval(active.timer)
    authorize(data.token ?? null)
  }
  //用戶端在App上取消
  else if (data.status === EnumQrcodeRespondStatus.CANCELLED) {
    stopPolling()
    cancel()
  }

}

//獲取二維碼
const fetch = () => {

  //清除blob (讓讀取轉動重轉)
  active.blob = null

  //清除狀態
  active.status = null

  //清除計時器
  clearInterval(active.timer)

  //停止上一輪的輪詢 (二維碼重取後 token/authorize 已被覆蓋)
  stopPolling()

  const xhr = new XMLHttpRequest()
  xhr.onload = () => {
    const reader = new FileReader()
    reader.onloadend = () => {
      //填入二維碼的blob
      active.blob = reader.result
      //監控超時
      active.timer = setInterval(()=>expire(), active.expire)
      //二維碼顯示後才開始輪詢掃碼狀態
      startPolling()
    }
    reader.readAsDataURL(xhr.response)
  }

  xhr.open('POST', `${import.meta.env.VITE_BASE_URL}qrcode/factory`)
  xhr.responseType = 'blob'
  xhr.setRequestHeader("Content-Type", "application/json; charset=UTF-8")
  xhr.setRequestHeader("X-Admin-Client", "1")
  xhr.send(JSON.stringify({
    "method": props.isMapping ? EnumQrcodeMethod.MAPPING : EnumQrcodeMethod.FACTORY,
    "token": active.token,
    "uuid": props.data.uuid,
    "organization": props.data.organization.uuid
  }))

}

//獲取圖像內容
const getImage = computed(() => {
  if (active.blob) return active.blob
  return factoryLoading
})

const getSubject = computed(()=>{
  if (props.isMapping) return `component.qrcode.mapping.subject`
  return `component.qrcode.login.subject`
})

const getText = computed(()=>{
  if (props.isMapping) return `component.qrcode.mapping.text`
  return `component.qrcode.login.text`
})

//是否二維碼載入成功
const isBlobLoaded = computed(() => {
  return !["", null].includes(active.blob)
})

//切換到密碼輸入的時候, 自動focus密碼框
watch(()=>active.passwd, async (e) => {
  if (e) {
    await nextTick()
    $(".el-input__inner", $(component.value)).focus()
  }
})

onMounted(async () => {

  active.token = getUuid(32, true)

  //首次獲取驗證二維碼
  setTimeout(() => fetch(), 300)

})

onUnmounted(()=>{

  //清除計時器
  if (active.timer) clearInterval(active.timer)

  //停止輪詢
  stopPolling()

})

</script>

<style scoped lang="scss">
.qrcode {
  .s5 {
    position: relative;

    .s7 {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: rgba(255, 255, 255, 0.975);

      .avatar {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 15px;
        width: 52px;
        height: 52px;
      }
    }

    img {
      &.p {
        width: 150px;
        height: 150px;
        margin: 35px;

        &.active {
          width: 220px;
          height: 220px;
          margin: 0;
          box-shadow: 0 4px 8px 0 rgb(34 41 47 / 12%), 0 2px 4px 0 rgb(34 41 47 / 8%);
        }

        &.swal-embedded {
          width: 100px;
          height: 100px;

          &.active {
            width: 170px;
            height: 170px;
          }
        }
      }
    }
  }

  .logo {
    border-radius: 35px;
    padding: 10px 15px;

    > span {
      position: relative;
      top: 1px;
    }

    > img {
      width: 20px;
      height: 20px;
      border-radius: 3px;
    }
  }
}

.dark-layout {
  .qrcode {
    .s5 {
      .s7 {
        background-color: rgba(40, 48, 70, 0.98);
      }

      img {
        &.p {
          &.active {
            box-shadow: 0 4px 8px 0 rgba(34, 41, 47, 0.75), 0 2px 4px 0 rgba(34, 41, 47, 0.75);
          }
        }
      }
    }
  }
}
</style>
